'use client';

import { NiceModal, Stack, Toast, Typography, useBreakpoints } from '@castlery/fortress';
import { CheckCircleFilled, Close } from '@castlery/fortress/Icons';
import { CustomLink, NextFortressLink, ScrollWrapper } from '@castlery/shared-components';
import { useEffect, useRef, useState } from 'react';
import { ProductItemDataProps } from './product-item';

import { EcEnv } from '@castlery/config';
import { removeFromWishlist } from '@castlery/modules-cms-services';
import {
  removeWishListItem,
  selectedTempRemoveWishListItemInfo,
  updateTempRemoveWishListItemInfo,
} from '@castlery/modules-user-domain';
import { useAppDispatch, useAppSelector } from '@castlery/shared-redux-store';
import { SingleProductItem as ProductItem } from './product-item';

interface ProductListProps {
  imageType?: 'base_image' | 'lifestyle_image';
  openHover?: boolean;
  products: ProductItemDataProps[];
  bgColor?: string;
  needSliderDisplay?: boolean;
  applyATCAndWishlist?: boolean;
  forceGridDisplay?: boolean;
  onClickSuccess?: ({ slotId }: { slotId: string }) => void;
}

const ProductList = ({
  imageType = 'base_image',
  openHover = false,
  products,
  bgColor,
  needSliderDisplay = true,
  applyATCAndWishlist = false,
  forceGridDisplay = false,
  onClickSuccess,
}: ProductListProps) => {
  const { desktop } = useBreakpoints();
  const [listWidth, setListWidth] = useState(1728);
  const stackRef = useRef<HTMLDivElement>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastStatus, setToastStatus] = useState<'add' | 'temp_remove' | 'remove'>('add');
  const [modalInfo, setModalInfo] = useState<{
    title: string;
    type: 'success' | 'error';
    confirmText?: string;
    cancelText: string;
    desc?: string;
  }>({
    title: 'Item has been added into your cart!',
    type: 'success',
    confirmText: 'View Cart',
    cancelText: 'Close',
  });
  const tempRemoveWishListItemInfo = useAppSelector(selectedTempRemoveWishListItemInfo);
  const lastTempRemoveWishListItemInfoRef = useRef({
    name: '',
    id: 0,
  });

  const dispatch = useAppDispatch();

  const [isTempRemoveToastOpen, setIsTempRemoveToastOpen] = useState(false);
  const [countDown, setCountDown] = useState(5);
  const countDownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const updateWidth = () => {
      if (stackRef.current) {
        const width = stackRef.current.offsetWidth;
        setListWidth(width);
      }
    };

    requestAnimationFrame(() => {
      updateWidth();

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setListWidth(entry.contentRect.width);
        }
      });

      if (stackRef.current) {
        resizeObserver.observe(stackRef.current);
      }

      return () => {
        resizeObserver.disconnect();
      };
    });
  }, []);

  const handleModalOpen = (value: boolean, type: 'success' | 'error', detail?: string) => {
    if (type === 'success') {
      setModalOpen(value);
      setModalInfo({
        title: 'Item has been added into your cart!',
        type: 'success',
        confirmText: 'View Cart',
        cancelText: 'Close',
      });
    } else {
      setModalOpen(value);
      setModalInfo({
        title: 'Unable to add item to cart',
        desc:
          detail ||
          'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
        type: 'error',
        confirmText: 'Close',
        cancelText: 'Close',
      });
    }
  };

  const renderModal = () => {
    return (
      <NiceModal
        title={modalInfo.title}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        success={modalInfo.type === 'success'}
        danger={modalInfo.type === 'error'}
        confirmText={modalInfo.confirmText}
        cancelText={modalInfo.cancelText}
        desc={modalInfo.desc}
        showCancelBtn={modalInfo.type === 'success'}
        onConfirm={() => {
          if (modalInfo.type === 'success') {
            window.location.href = `${
              EcEnv.NEXT_PUBLIC_ONEPIECE_HOST
            }/${EcEnv.NEXT_PUBLIC_COUNTRY.toLocaleLowerCase()}/cart`;
          } else {
            setModalOpen(false);
          }
        }}
        onCancel={() => {
          setModalOpen(false);
        }}
      />
    );
  };

  const handleToastOpen = (status: 'add' | 'remove' | 'temp_remove', info?: { id: number; name: string }) => {
    if (status === 'add') {
      setToastStatus('add');
      setToastOpen(true);
      setTimeout(() => {
        setToastOpen(false);
      }, 3000);
    } else {
      setToastStatus('temp_remove');
      setIsTempRemoveToastOpen(true);
      if (lastTempRemoveWishListItemInfoRef.current.id !== 0) {
        removeFromWishlist({ id: lastTempRemoveWishListItemInfoRef.current.id });
        dispatch(removeWishListItem(lastTempRemoveWishListItemInfoRef.current.id));
      }
      lastTempRemoveWishListItemInfoRef.current = {
        name: info.name || '',
        id: info.id || 0,
      };
      if (countDownTimerRef.current) {
        clearInterval(countDownTimerRef.current);
        countDownTimerRef.current = null;
      }
      setCountDown(5);
      countDownTimerRef.current = setInterval(() => {
        setCountDown((prev) => {
          if (prev <= 1) {
            if (countDownTimerRef.current) {
              removeFromWishlist({ id: info.id });
              dispatch(removeWishListItem(info.id));
              setIsTempRemoveToastOpen(false);
              lastTempRemoveWishListItemInfoRef.current = {
                name: '',
                id: 0,
              };
              clearInterval(countDownTimerRef.current);
              countDownTimerRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };

  const renderTempRemoveToast = () => {
    if (!desktop) {
      return (
        <Toast
          open={isTempRemoveToastOpen}
          theme="dark"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          sx={{
            width: '342px',
          }}
          actionSlot={
            <Typography
              sx={{
                color: '#F6F3E7',
                fontSize: '16px',
                textDecoration: 'underline',
                cursor: 'pointer',
                textDecorationColor: '#F6F3E7',
              }}
              onClick={() => {
                if (countDownTimerRef.current) {
                  clearInterval(countDownTimerRef.current);
                  countDownTimerRef.current = null;
                  setIsTempRemoveToastOpen(false);
                  lastTempRemoveWishListItemInfoRef.current = {
                    name: '',
                    id: 0,
                  };
                  dispatch(updateTempRemoveWishListItemInfo({ name: '', id: 0 }));
                }
              }}
            >
              {`Undo (0:0${countDown})`}
            </Typography>
          }
        >
          <Typography
            sx={(theme) => ({
              color: theme.palette.brand.warmLinen[500],
              fontSize: '16px',
            })}
          >
            {`${tempRemoveWishListItemInfo.name} has been removed from your wishlist.`}
          </Typography>
        </Toast>
      );
    }
  };

  const renderToast = () => {
    if (!desktop) {
      return (
        <Toast
          open={toastOpen}
          theme="dark"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          sx={{
            width: '342px',
          }}
          startDecorator={<CheckCircleFilled />}
          endDecorator={<Close onClick={() => setToastOpen(false)} />}
          actionSlot={
            toastStatus === 'remove' ? null : (
              <NextFortressLink
                href={`${EcEnv.NEXT_PUBLIC_ONEPIECE_HOST}/${EcEnv.NEXT_PUBLIC_COUNTRY.toLocaleLowerCase()}/wishlist`}
              >
                <Typography
                  sx={{
                    color: '#F6F3E7',
                    fontSize: '16px',
                  }}
                >
                  View Wishlist
                </Typography>
              </NextFortressLink>
            )
          }
        >
          <Typography
            sx={(theme) => ({
              color: theme.palette.brand.warmLinen[500],
              fontSize: '16px',
            })}
          >
            {toastStatus === 'remove' ? 'Removed from wishlist!' : 'Added to wishlist!'}
          </Typography>
        </Toast>
      );
    }
  };

  if (forceGridDisplay || (!needSliderDisplay && desktop)) {
    return (
      <Stack
        direction="row"
        sx={{
          width: '100%',
          padding: '0 32px',
          flexWrap: 'wrap',
        }}
        ref={stackRef}
      >
        {products.map((product, index) => {
          return (
            <Stack
              key={index}
              sx={{
                flex: '0 0 25%',
                ...(!desktop && {
                  flex: '0 0 50%',
                  maxWidth: '50%',
                }),
                alignItems: 'stretch',
                display: 'flex',
              }}
            >
              <ProductItem
                key={index}
                length={(listWidth - (desktop ? 100 : 12)) / (desktop ? 4 : 2)}
                product={product}
                openHover={openHover}
                imageType={imageType}
                bgColor={bgColor}
                needMargin={index % (desktop ? 4 : 2) !== (desktop ? 3 : 1) ? true : false}
                onModalOpen={handleModalOpen}
                onToastOpen={handleToastOpen}
                applyATCAndWishlist={applyATCAndWishlist}
                onClickSuccess={onClickSuccess}
              />
            </Stack>
          );
        })}
        {renderModal()}
        {renderToast()}
        {renderTempRemoveToast()}
      </Stack>
    );
  }
  return (
    <Stack
      sx={(theme) => ({
        mt: '32px',
        width: '100%',
        ...(!desktop && {
          mt: '16px',
        }),
      })}
    >
      <ScrollWrapper hideTrack={false} hideDesktopAction={true} hideBottomAction={true}>
        <Stack
          direction="row"
          sx={{
            width: 'fit-content',
            minWidth: '100%',
            gap: desktop ? 6 : 3,
          }}
        >
          {products.map((product, index) => {
            return (
              <ProductItem
                key={index}
                product={product}
                openHover={openHover}
                imageType={imageType}
                bgColor={bgColor}
                onModalOpen={handleModalOpen}
                onToastOpen={handleToastOpen}
                applyATCAndWishlist={applyATCAndWishlist}
                onClickSuccess={onClickSuccess}
              />
            );
          })}
        </Stack>
      </ScrollWrapper>
      {renderModal()}
      {renderToast()}
      {renderTempRemoveToast()}
    </Stack>
  );
};

export { ProductList };
